/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { TEACHER_ROLES, STUDENT_ROLES, ADMIN_ROLES, MANAGER_ROLES, PLATFORM_ADMIN_ROLES, SCOLARITE_ROLES, STAFF_ROLES, IT_ROLE, ACADEMIC_STAFF_ROLES, TEACHER_DOUBLE_HAT_ROLES } from '../constants/roleGroups';
import { useTenant } from '../context/TenantContext';
import { hasLegacyRole, hasSystemRole } from '../utils/roleAccess';
import useMasterShellAuthority, { invalidateMasterShellAuthorityCache } from '../components/security/useMasterShellAuthority';
import { decideMasterShellAccess } from '../components/security/masterShellAccess';
import { canImpersonateWith } from '../services/impersonationPolicy';
import { invalidateMasterAccessScopeCache } from './useMasterAccessScope';
import { invalidateMasterPortalRoleCache } from './useMasterPortalRole';
import { invalidateR4RAccessCache } from './useR4RAccess';

/**
 * Calcule la liste des rôles/comptes disponibles pour la bascule via l'avatar utilisateur.
 * Gère les comptes cumulatifs (ex: Responsable Académique + Enseignant, Personnel + Étudiant, Enseignant + Étudiant).
 */
export const computeAvailableRoles = (user) => {
  if (!user || !user.role) return [];
  const list = [];
  const add = (r) => {
    if (r && typeof r === 'string' && !list.includes(r)) {
      list.push(r);
    }
  };

  // 1. Rôle principal
  add(user.role);
  if (user.primary_role) add(user.primary_role);

  // 2. Rôles secondaires déclarés
  if (user.secondary_role) {
    if (Array.isArray(user.secondary_role)) {
      user.secondary_role.forEach(add);
    } else {
      add(user.secondary_role);
    }
  }
  if (Array.isArray(user.secondary_roles)) {
    user.secondary_roles.forEach(add);
  }
  if (Array.isArray(user.available_roles)) {
    user.available_roles.forEach(add);
  }

  // 3. Drapeaux explicites (is_teacher / is_student)
  if (user.is_teacher) add('Enseignant');
  if (user.is_student) add('Apprenant');

  // 4. Déduction métier institutionnelle :
  // Les rôles de direction/coordination qui enseignent automatiquement
  if (hasLegacyRole(user, TEACHER_DOUBLE_HAT_ROLES) && !list.includes('Enseignant')) {
    add('Enseignant');
  }

  return list;
};

// Règles hiérarchiques d'impersonification.
//
// P0 frontière plateforme, sous-lot D (2026-07-23) : la valeur `null` ne signifie
// PLUS « toutes les cibles » — aucune valeur sentinelle ne le signifie. Chaque
// entrée est un TABLEAU explicite de rôles impersonifiables (absence de règle =
// AUCUN droit, fail-closed). Les entrées `Master: null` et `IT: null` sont
// SUPPRIMÉES :
//   · l'impersonation « toutes cibles » ne reste accessible qu'à l'autorité
//     plateforme CANONIQUE (super actif, cf. canImpersonate) — jamais au libellé
//     'Master' ni à `tenant_id IS NULL` ;
//   · les rôles IT tenant (IT / Administrateur / it_admin) n'ont PLUS aucun droit
//     d'impersonation. Toute impersonation IT tenant sera réévaluée si un besoin
//     métier documenté la justifie (post-pilote).
// 'Parent' est un rôle virtuel (ghost) — pas de profil réel dans profiles.
// eslint-disable-next-line react-refresh/only-export-components
export const IMPERSONATION_RULES = {
  'DG': [
    'Direction Générale', 'Directeur', 'Direction des écoles',
    'Responsable Académique', 'Gestionnaire Scolarité', 'Comptable',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
    'Coordonnateur de Cycle', 'Chargé Académique', 'Manager Qualité', 'Responsable Qualité',
  ],
  'Direction Générale': [
    'Directeur', 'Direction des écoles', 'Responsable Académique',
    'Gestionnaire Scolarité', 'Comptable',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
    'Coordonnateur de Cycle', 'Chargé Académique', 'Manager Qualité', 'Responsable Qualité',
  ],
  'Directeur': [
    'Responsable Académique', 'Gestionnaire Scolarité',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
    'Chargé Académique', 'Coordonnateur de Cycle',
  ],
  'Direction des écoles': [
    'Responsable Académique', 'Gestionnaire Scolarité',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Directeur des écoles': [
    'Responsable Académique', 'Gestionnaire Scolarité',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Directeur Académique': [
    'Responsable Académique', 'Gestionnaire Scolarité',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  "Directeur d'École": [
    'Responsable Académique', 'Gestionnaire Scolarité',
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Responsable Académique': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  // Même droit étendu au reste du personnel académique (signalé par Alfred,
  // 2026-10-08 : "le personnel académique doit pouvoir impersonifier les
  // cockpits étudiants et enseignants") — seul 'Responsable Académique'
  // l'avait jusqu'ici, les autres rôles d'ACADEMIC_STAFF_ROLES (roleGroups.js)
  // n'avaient AUCUNE règle, donc aucun droit (fail-closed par défaut).
  'Doyen': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Chef de Département': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Chargé Académique': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Responsable de Filière': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Coordonnateur de Cycle': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Coordonnateur de Programme': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Coordonnateur de Niveau': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Gestionnaire Scolarité': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Registraire': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
  'Registraire Institutionnel': [
    'Enseignant', 'Enseignant Permanent', 'Enseignant Associé',
    'Apprenant', 'Parent',
  ],
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { reloadTenant, stopTenantInspection } = useTenant();
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('rectorium_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    const saved = localStorage.getItem('rectorium_impersonation');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      let session = null;
      try {
        ({ data: { session } } = await supabase.auth.getSession());
      } catch (e) {
        // getSession rejette quand le refresh token est invalide (403) : sans garde,
        // autoRefreshToken relance la requête en boucle → tempête 403 + contention de
        // verrou → l'onglet est tué (RESULT_CODE_KILLED). On purge le token LOCAL
        // (scope:'local' n'affecte pas les sessions PIN héritées) pour couper la
        // boucle, puis on renvoie proprement vers la connexion.
        try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* best effort */ }
        localStorage.removeItem('rectorium_user');
        setUser(null);
        if (typeof window !== 'undefined' && !/\/login|\/invite|\/signup|^\/$/.test(window.location.pathname)) {
          window.location.replace('/login');
        }
        setLoading(false);
        return;
      }
      if (session?.user) {
        let { data: profile } = await supabase
          .from('profiles')
          .select('*, schools(*), campuses(*)')
          .eq('id', session.user.id)
          .maybeSingle();
        // Même repli que adoptSession() : un profil qui ne revient pas (ligne
        // absente, ou embed schools(*)/campuses(*) en echec si la relation
        // FK venait a manquer un jour) ne doit jamais laisser `user` bloque a
        // null indefiniment — cf. chantier checksession-schools-fk-absente.
        if (!profile) {
          const { data: fallback } = await supabase.rpc('resolve_profile_for_oauth_session');
          profile = Array.isArray(fallback) ? fallback[0] : fallback;
        }
        if (profile) {
          const fullUser = { ...session.user, ...profile };
          setUser(fullUser);
          localStorage.setItem('rectorium_user', JSON.stringify(fullUser));
        }
      } else {
        // Session nulle (refresh expiré, signOut depuis un autre appareil) :
        // l'utilisateur mis en cache ne doit pas survivre, sinon ProtectedRoute
        // laisse passer et tout le tableau de bord tourne en anon, qui n'a plus
        // aucun droit sur public (chantier session-zombie-localstorage).
        localStorage.removeItem('rectorium_user');
        localStorage.removeItem('rectorium_impersonation');
        setUser(null);
        setImpersonatedUser(null);
      }
      setLoading(false);
    };
    checkSession();

    // eslint-disable-next-line no-unused-vars
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        localStorage.removeItem('rectorium_user');
        localStorage.removeItem('rectorium_impersonation');
        setUser(null);
        setImpersonatedUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // ── Impersonification ────────────────────────────────────────────────────

  // Autorité plateforme CANONIQUE (super actif) — même signal que la garde du
  // Master Shell (sous-lot F). Seule cette autorité peut impersonifier « toutes
  // cibles » ; jamais le libellé 'Master', jamais tenant_id IS NULL.
  const shellAuthority = useMasterShellAuthority();
  const isPlatformSuper = decideMasterShellAccess(shellAuthority) === 'allow';

  // Vérifie si le vrai utilisateur peut impersonifier un rôle cible. La décision
  // est déléguée à une fonction PURE (fail-closed, testable). Voir
  // src/services/impersonationPolicy.js.
  const canImpersonate = useCallback((targetRole) => canImpersonateWith({
    userRole: user?.role,
    targetRole,
    isPlatformSuper,
    rules: IMPERSONATION_RULES,
  }), [user, isPlatformSuper]);

  const startImpersonation = useCallback(async (targetProfile) => {
    if (!canImpersonate(targetProfile.role)) {
      throw new Error('Impersonification non autorisée pour ce profil');
    }
    setImpersonatedUser(targetProfile);
    localStorage.setItem('rectorium_impersonation', JSON.stringify(targetProfile));
    try {
      await supabase.from('access_logs').insert({
        user_id: user.id,
        tenant_id: user.tenant_id,
        action: 'IMPERSONATION_START',
        resource: 'profiles',
        metadata: {
          target_user_id: targetProfile.id ?? null,
          target_role: targetProfile.role,
          target_name: targetProfile.full_name,
          is_ghost: targetProfile.isGhost ?? false,
        },
      });
    } catch { /* audit non bloquant */ }
  }, [user, canImpersonate]);

  const stopImpersonation = useCallback(async () => {
    if (!impersonatedUser) return;
    try {
      await supabase.from('access_logs').insert({
        user_id: user.id,
        tenant_id: user.tenant_id,
        action: 'IMPERSONATION_END',
        resource: 'profiles',
        metadata: {
          target_user_id: impersonatedUser.id ?? null,
          target_role: impersonatedUser.role,
          target_name: impersonatedUser.full_name,
        },
      });
    } catch { /* audit non bloquant */ }
    setImpersonatedUser(null);
    localStorage.removeItem('rectorium_impersonation');
  }, [user, impersonatedUser]);

  // ── Auth actions ─────────────────────────────────────────────────────────
  //
  // La connexion par IDENTIFIANT + PIN a été RETIRÉE (chantier « identité
  // unifiée », 2026-07-27, décision Alfred). Toute authentification passe
  // désormais par Supabase Auth : email/mot de passe, Google, ou lien
  // magique (parents). Une seule identité — `auth.uid()` — vaut partout, côté
  // serveur (resolve_session, 44 policies RLS, autorité plateforme) comme côté
  // navigateur. Les comptes sont créés par INVITATION email.

  // ── Auth publique Supabase (funnel Free — PARCOURS_INSCRIPTION_FREE.md) ──

  // Charge le profil lié à une session Supabase Auth et l'adopte comme user.
  // Retourne 'ready' (profil trouvé), 'onboarding' (compte sans établissement)
  // ou null (pas de session).
  const adoptSession = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    let { data: profile } = await supabase
      .from('profiles')
      .select('*, schools(*), campuses(*)')
      .eq('id', session.user.id)
      .maybeSingle();
    // Repli id-ou-email (compte créé côté PIN, id indépendant de auth.users
    // mais même email) — SECURITY DEFINER pour contourner la RLS qui, sinon,
    // bloquerait la lecture de cette ligne (id != auth.uid()).
    if (!profile) {
      const { data: fallback } = await supabase.rpc('resolve_profile_for_oauth_session');
      profile = Array.isArray(fallback) ? fallback[0] : fallback;
    }
    if (!profile) return 'onboarding';
    const fullUser = { ...session.user, ...profile };
    setUser(fullUser);
    localStorage.setItem('rectorium_user', JSON.stringify(fullUser));
    await reloadTenant();
    return 'ready';
  }, [reloadTenant]);

  // Inscription email + mot de passe (Turnstile obligatoire, vérification email
  // requise avant toute connexion — Supabase Auth « Confirm email »).
  const signUpWithEmail = async (email, password, captchaToken) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          captchaToken,
          emailRedirectTo: `${window.location.origin}/login?verified=1`,
        },
      });
      if (signUpError) throw new Error(signUpError.message);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithEmail = async (email, password, captchaToken) => {
    setLoading(true);
    setError(null);
    setImpersonatedUser(null);
    localStorage.removeItem('rectorium_impersonation');
    try {
      const typedEmail = email.trim().toLowerCase();
      // Email secondaire de connexion : résout vers l'email principal
      // (auth.users.email) si l'utilisateur a enregistré typedEmail comme
      // second email. RPC public anon, retombe sur typedEmail si pas de match.
      let loginEmail = typedEmail;
      try {
        const { data: resolved } = await supabase.rpc('resolve_login_email', { input_email: typedEmail });
        if (resolved) loginEmail = resolved;
      } catch { /* pas de second email enregistré, on continue avec typedEmail */ }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password,
        options: { captchaToken },
      });
      if (signInError) {
        if (/email not confirmed/i.test(signInError.message)) {
          const e = new Error('Votre email n’est pas encore vérifié. Cliquez sur le lien reçu par email ou demandez un nouvel envoi.');
          e.code = 'email_not_confirmed';
          throw e;
        }
        throw new Error('Email ou mot de passe incorrect');
      }
      const status = await adoptSession();
      if (status === 'ready') {
        const { data: { session } } = await supabase.auth.getSession();
        Promise.resolve(supabase.from('audit_logs').insert({
          user_id: session.user.id,
          action: 'USER_LOGIN',
          table_name: 'profiles',
          record_id: session.user.id,
          user_agent: navigator.userAgent,
        })).catch(() => {});
        // Metering facturation : conservé depuis l'ancien flux PIN retiré, pour
        // que les analyses d'usage ne perdent pas les connexions.
        const saved = localStorage.getItem('rectorium_user');
        const profile = saved ? JSON.parse(saved) : null;
        Promise.resolve(supabase.from('usage_events').insert({
          tenant_id:  profile?.tenant_id ?? null,
          user_id:    session.user.id,
          event_type: 'user_login',
          metadata:   { role: profile?.role ?? null },
        })).catch(() => {});
      }
      return status; // 'ready' → /dashboard, 'onboarding' → /onboarding
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // OAuth Google : email pré-vérifié par Google, sans CAPTCHA (spéc §1.1).
  const loginWithGoogle = async () => {
    setError(null);
    // `prompt: 'select_account'` (signalé par Alfred, 2026-10-08 : "je me
    // déconnecte, je clique sur Google, ça m'amène directement sur [mon
    // autre compte]") — sans ce paramètre, Google réutilise en silence la
    // session Google déjà active dans le navigateur au lieu de proposer un
    // choix, ce qui empêche de revenir sur un compte Google différent (ex.
    // un tenant client) sans se déconnecter de Google lui-même en plus de
    // l'app.
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback`, queryParams: { prompt: 'select_account' } },
    });
    if (oauthError) {
      setError(oauthError.message);
      throw new Error(oauthError.message);
    }
  };

  const resendVerificationEmail = async (email, captchaToken) => {
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim().toLowerCase(),
      options: { emailRedirectTo: `${window.location.origin}/login?verified=1`, captchaToken },
    });
    if (resendError) throw new Error(resendError.message);
  };

  const requestPasswordReset = async (email, captchaToken) => {
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: `${window.location.origin}/reset-password`, captchaToken },
    );
    if (resetError) throw new Error(resetError.message);
  };

  const updatePassword = async (newPassword) => {
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) throw new Error(updateError.message);
  };

  const [activeRoleOverride, setActiveRoleOverride] = useState(() => {
    return localStorage.getItem('rectorium_active_role') || null;
  });

  const availableRoles = useMemo(() => computeAvailableRoles(user), [user]);

  const switchActiveRole = useCallback((targetRole) => {
    const primary = user?.role;
    if (!targetRole || targetRole === primary) {
      setActiveRoleOverride(null);
      localStorage.removeItem('rectorium_active_role');
    } else {
      setActiveRoleOverride(targetRole);
      localStorage.setItem('rectorium_active_role', targetRole);
    }
  }, [user]);

  useEffect(() => {
    if (activeRoleOverride && availableRoles.length > 0 && !availableRoles.includes(activeRoleOverride)) {
      setActiveRoleOverride(null);
      localStorage.removeItem('rectorium_active_role');
    }
  }, [availableRoles, activeRoleOverride]);

  const logout = async () => {
    // Arrêter l'impersonification et purger le rôle actif avant de déconnecter
    if (impersonatedUser) {
      await stopImpersonation();
    }
    setActiveRoleOverride(null);
    localStorage.removeItem('rectorium_active_role');

    if (user) {
      await supabase.from('audit_logs').insert({
        user_id: user.id,
        action: 'USER_LOGOUT',
        table_name: 'profiles',
        record_id: user.id,
        user_agent: navigator.userAgent,
      });
    }
    // Sortir de l'inspection AVANT signOut : après, la RPC, le comptage de
    // contrôle et la trace de restauration partiraient en anon et seraient
    // refusés (chantier inspection-master-fonction-pin-disparue).
    await stopTenantInspection();
    await supabase.auth.signOut();
    setUser(null);
    setImpersonatedUser(null);
    localStorage.removeItem('rectorium_user');
    localStorage.removeItem('rectorium_impersonation');
    // Caches module Master/R4R keyés par email/session : sans invalidation ici,
    // la prochaine connexion (compte différent, même onglet) verrait encore le
    // périmètre/portail/accès R4R de la session précédente (état déjà chargé
    // au montage, cf. useMasterAccessScope.js et consorts).
    invalidateMasterAccessScopeCache();
    invalidateMasterPortalRoleCache();
    invalidateR4RAccessCache();
    // Sans cette invalidation, le verdict d'autorité plateforme de la session
    // précédente survivait en mémoire : une reconnexion avec un autre compte
    // dans le même onglet héritait de son refus (ou de son accès) au Master
    // Shell jusqu'à un rechargement complet.
    invalidateMasterShellAuthorityCache();
    await reloadTenant();
  };

  const updateUser = (updatedData) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updatedData };
      localStorage.setItem('rectorium_user', JSON.stringify(updated));
      return updated;
    });
  };

  // ── Utilisateur effectif (impersonné, basculé ou réel) ────────────────────
  const baseEffectiveUser = impersonatedUser ?? user;
  const effectiveUser = useMemo(() => {
    if (!baseEffectiveUser) return null;
    if (activeRoleOverride && !impersonatedUser) {
      return {
        ...baseEffectiveUser,
        role: activeRoleOverride,
        is_switched_role: true,
        primary_role: user?.role,
      };
    }
    return baseEffectiveUser;
  }, [baseEffectiveUser, activeRoleOverride, impersonatedUser, user?.role]);

  const value = {
    // `user` = utilisateur effectif (rôle vu par l'UI)
    user: effectiveUser,
    // `originalUser` = vrai compte connecté (pour les checks d'autorisation)
    originalUser: user,
    impersonatedUser,
    isImpersonating: !!impersonatedUser,
    canImpersonate,
    startImpersonation,
    stopImpersonation,
    loading,
    error,
    logout,
    updateUser,
    // Bascule de rôle / multi-compte
    availableRoles,
    activeRole: effectiveUser?.role || null,
    primaryRole: user?.role || null,
    isRoleSwitched: Boolean(activeRoleOverride && !impersonatedUser),
    switchActiveRole,
    // Funnel public Free (Supabase Auth)
    adoptSession,
    signUpWithEmail,
    loginWithEmail,
    loginWithGoogle,
    resendVerificationEmail,
    requestPasswordReset,
    updatePassword,
    // Rôles calculés sur effectiveUser (comportement identique à avant pour le reste du code)
    isAdmin: ADMIN_ROLES.includes(effectiveUser?.role),
    isDirector: MANAGER_ROLES.includes(effectiveUser?.role),
    isQuality: ['Manager Qualité', 'Responsable Qualité'].includes(effectiveUser?.role),
    isScolarite: SCOLARITE_ROLES.includes(effectiveUser?.role),
    isTeacher: TEACHER_ROLES.includes(effectiveUser?.role),
    isStudent: STUDENT_ROLES.includes(effectiveUser?.role),
    canEdit: () => {
      if (!effectiveUser) return false;
      return ADMIN_ROLES.includes(effectiveUser.role) || MANAGER_ROLES.includes(effectiveUser.role);
    },
    canManageGovernance: PLATFORM_ADMIN_ROLES.includes(effectiveUser?.role),
    // Flags plateforme — P0 sous-lot D (2026-07-23) : `isPlatformAdmin` dérive
    // désormais du signal CANONIQUE (super actif, rectorium_team_members), plus
    // du libellé 'Master' + tenant_id null. Aligné sur auth_is_platform_admin()
    // serveur et sur la garde du Master Shell (F). Fail-closed pendant le
    // chargement de l'autorité.
    isPlatformAdmin: isPlatformSuper,
    isMasterAccount: hasSystemRole(user, 'master'),
    // Peut gérer les comptes admin (Master + rôles DG-équivalents, hors IT)
    canManageAdminAccounts: ADMIN_ROLES.includes(user?.role) && !hasLegacyRole(user, IT_ROLE),
    // Droit d'impersonifier (basé sur le vrai compte, jamais sur effectiveUser)
    // FAIL-CLOSED : droit d'impersonation ssi super canonique OU règle-tableau
    // explicite. Une entrée absente/null ne confère aucun droit.
    hasImpersonationRights: isPlatformSuper || Array.isArray(IMPERSONATION_RULES[user?.role]),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

/** Variante non bloquante : null hors AuthProvider (composants partagés/testés isolément). */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalAuth = () => useContext(AuthContext);
